import { db as firebaseDb } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { FormTemplate, FormSection, TemplateSettings } from '@easydone/shared';

// Re-export types for convenience
export type { FormTemplate } from '@easydone/shared';

/**
 * Get all templates for an organization
 */
export async function getTemplates(orgId: string): Promise<FormTemplate[]> {
  const templatesRef = collection(firebaseDb, 'organizations', orgId, 'templates');
  const q = query(templatesRef, orderBy('name', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as FormTemplate));
}

/**
 * Get active templates only
 */
export async function getActiveTemplates(orgId: string): Promise<FormTemplate[]> {
  const templatesRef = collection(firebaseDb, 'organizations', orgId, 'templates');
  const q = query(
    templatesRef,
    where('status', '==', 'active'),
    orderBy('name', 'asc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as FormTemplate));
}

/**
 * Get templates by category
 */
export async function getTemplatesByCategory(
  orgId: string,
  category: string
): Promise<FormTemplate[]> {
  const templatesRef = collection(firebaseDb, 'organizations', orgId, 'templates');
  const q = query(
    templatesRef,
    where('category', '==', category),
    orderBy('name', 'asc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as FormTemplate));
}

/**
 * Get a single template by ID
 */
export async function getTemplate(
  orgId: string,
  templateId: string
): Promise<FormTemplate | null> {
  const templateRef = doc(firebaseDb, 'organizations', orgId, 'templates', templateId);
  const snapshot = await getDoc(templateRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as FormTemplate;
}

/**
 * Template creation data
 */
export interface CreateTemplateData {
  name: string;
  description?: string;
  category: string;
  assetTypes?: string[];
  sections: FormSection[];
  settings?: TemplateSettings;
  isPublic?: boolean;
}

/**
 * Create a new template
 */
export async function createTemplate(
  orgId: string,
  data: CreateTemplateData
): Promise<FormTemplate> {
  const templatesRef = collection(firebaseDb, 'organizations', orgId, 'templates');

  const templateData = {
    ...data,
    orgId,
    status: 'active' as const,
    version: 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(templatesRef, templateData);
  const snapshot = await getDoc(docRef);

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as FormTemplate;
}

/**
 * Update a template
 */
export async function updateTemplate(
  orgId: string,
  templateId: string,
  data: Partial<CreateTemplateData>
): Promise<void> {
  const templateRef = doc(firebaseDb, 'organizations', orgId, 'templates', templateId);

  // Increment version when updating
  const currentTemplate = await getDoc(templateRef);
  const currentVersion = currentTemplate.data()?.version || 1;

  await updateDoc(templateRef, {
    ...data,
    version: currentVersion + 1,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Duplicate a template
 */
export async function duplicateTemplate(
  orgId: string,
  templateId: string,
  newName: string
): Promise<FormTemplate> {
  const template = await getTemplate(orgId, templateId);
  if (!template) {
    throw new Error('Template not found');
  }

  const duplicateData: CreateTemplateData = {
    name: newName,
    description: template.description,
    category: template.category,
    assetTypes: template.assetTypes,
    sections: template.sections,
    isPublic: false,
  };

  return await createTemplate(orgId, duplicateData);
}

/**
 * Archive a template (soft delete)
 */
export async function archiveTemplate(orgId: string, templateId: string): Promise<void> {
  const templateRef = doc(firebaseDb, 'organizations', orgId, 'templates', templateId);
  await updateDoc(templateRef, {
    status: 'archived',
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete a template (hard delete)
 */
export async function deleteTemplate(orgId: string, templateId: string): Promise<void> {
  const templateRef = doc(firebaseDb, 'organizations', orgId, 'templates', templateId);
  await deleteDoc(templateRef);
}

/**
 * Validate template structure
 */
export function validateTemplate(template: Partial<FormTemplate>): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!template.name || template.name.trim() === '') {
    errors.push('Template name is required');
  }

  if (!template.category || template.category.trim() === '') {
    errors.push('Category is required');
  }

  if (!template.sections || template.sections.length === 0) {
    errors.push('At least one section is required');
  } else {
    template.sections.forEach((section, sectionIndex) => {
      if (!section.title || section.title.trim() === '') {
        errors.push(`Section ${sectionIndex + 1}: Title is required`);
      }

      // Section must have either fields or measurements
      const hasFields = section.fields && section.fields.length > 0;
      const hasMeasurements = section.measurements && section.measurements.length > 0;

      if (!hasFields && !hasMeasurements) {
        errors.push(`Section ${sectionIndex + 1}: At least one field or measurement is required`);
      }

      // Validate fields if present
      if (section.fields && section.fields.length > 0) {
        section.fields.forEach((field, fieldIndex) => {
          if (!field.label || field.label.trim() === '') {
            errors.push(
              `Section ${sectionIndex + 1}, Field ${fieldIndex + 1}: Label is required`
            );
          }

          if (!field.type) {
            errors.push(
              `Section ${sectionIndex + 1}, Field ${fieldIndex + 1}: Type is required`
            );
          }

          // Validate dropdown/radio options
          if (
            (field.type === 'dropdown' || field.type === 'radio') &&
            (!field.options || field.options.length === 0)
          ) {
            errors.push(
              `Section ${sectionIndex + 1}, Field ${fieldIndex + 1}: Options are required for ${field.type} fields`
            );
          }
        });
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}
